"use client";

import { useSyncExternalStore } from "react";
import {
  AI_CHAT_AUTO_APPROVE_TOOLS_STORAGE_KEY,
  AI_CHAT_MODEL_STORAGE_KEY,
} from "@/lib/ai/constants";
import { type ModelId, normalizeModelId } from "@/lib/ai/models";
import { StorageManager } from "@/lib/storage/local-storage";

const AUTO_APPROVE_PREFERENCE_CHANGE_EVENT =
  "amethyst:ai-chat-auto-approve-preference-change";

/**
 * chat surface 間で共有する model preference を storage から読む。
 *
 * page / side panel provider 側では保存値の解釈ではなく、いつ再同期するかに
 * 集中できるように storage decode を 1 箇所に寄せている。
 */
export function readStoredChatModel(): ModelId | null {
  const savedModel = StorageManager.getItem(AI_CHAT_MODEL_STORAGE_KEY);
  const normalizedModel = normalizeModelId(savedModel);
  if (savedModel && normalizedModel && savedModel !== normalizedModel) {
    StorageManager.setItem(AI_CHAT_MODEL_STORAGE_KEY, normalizedModel);
  }
  return normalizedModel;
}

export function writeStoredChatModel(modelId: ModelId) {
  StorageManager.setItem(AI_CHAT_MODEL_STORAGE_KEY, modelId);
}

function readStoredAutoApproveToolConfirmations(): boolean | null {
  const raw = StorageManager.getItem(AI_CHAT_AUTO_APPROVE_TOOLS_STORAGE_KEY);
  if (raw === null) {
    return null;
  }
  return raw === "true";
}

export function writeStoredAutoApproveToolConfirmations(enabled: boolean) {
  StorageManager.setItem(
    AI_CHAT_AUTO_APPROVE_TOOLS_STORAGE_KEY,
    enabled ? "true" : "false",
  );
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(AUTO_APPROVE_PREFERENCE_CHANGE_EVENT));
  }
}

function subscribeToAutoApprovePreference(onStoreChange: () => void) {
  const handleStorage = (event: StorageEvent) => {
    if (event.key === AI_CHAT_AUTO_APPROVE_TOOLS_STORAGE_KEY) {
      onStoreChange();
    }
  };

  window.addEventListener("storage", handleStorage);
  window.addEventListener(AUTO_APPROVE_PREFERENCE_CHANGE_EVENT, onStoreChange);

  return () => {
    window.removeEventListener("storage", handleStorage);
    window.removeEventListener(
      AUTO_APPROVE_PREFERENCE_CHANGE_EVENT,
      onStoreChange,
    );
  };
}

function getAutoApprovePreferenceSnapshot() {
  return readStoredAutoApproveToolConfirmations() ?? false;
}

/**
 * page / side panel で共有する自動承認 preference を購読する。
 *
 * SSR と hydration の初期スナップショットを false に固定し、保存値は hydration
 * 後に外部ストアとして反映することで、localStorage の有無による markup 差を防ぐ。
 */
export function useStoredAutoApproveToolConfirmations() {
  return useSyncExternalStore(
    subscribeToAutoApprovePreference,
    getAutoApprovePreferenceSnapshot,
    () => false,
  );
}
